import type {
  FirstRunInputSubmission,
  SpecificationFieldValue,
} from '../components/drbfm/first-run-input/types';
import type { AnalysisSources, DRBFMInputData } from '../types';

const pinnedVersion = (value: { versionId: string | null }) => {
  if (!value.versionId) throw new Error('source_version_required');
  return value.versionId;
};

export function registeredSubmission(input: FirstRunInputSubmission): DRBFMInputData {
  const spec = input.specifications;
  const common = { itemName: input.itemName, title: input.title,
    oldSpecification: '', newSpecification: '' };
  const derivedFrom: AnalysisSources['derivedFrom'] = {};
  const recordOrigin = (role: 'old' | 'new' | 'report', value: SpecificationFieldValue) => {
    if (value.kind === 'derived')
      derivedFrom[role] = {
        documentId: value.origin.documentId,
        versionId: pinnedVersion(value.origin),
      };
  };
  if (spec.mode === 'old_new') {
    recordOrigin('old', spec.old);
    recordOrigin('new', spec.new);
  } else recordOrigin('report', spec.difference);
  const context = {
    supplement: input.supplement,
    ...(Object.keys(derivedFrom).length ? { derivedFrom } : {}),
  };
  let sources: AnalysisSources;
  if (spec.mode === 'difference') {
    sources =
      spec.difference.kind === 'document'
        ? {
            ...context,
            pattern: 'change_report',
            reportDocumentId: spec.difference.documentId,
            expectedVersions: { report: pinnedVersion(spec.difference) },
          }
        : {
            ...context,
            pattern: 'change_report_text',
            reportText: spec.difference.text,
          };
  } else if (spec.old.kind === 'document' && spec.new.kind === 'document') {
    sources = {
      ...context,
      pattern: 'old_new_documents',
      oldDocumentId: spec.old.documentId,
      newDocumentId: spec.new.documentId,
      expectedVersions: {
        old: pinnedVersion(spec.old),
        new: pinnedVersion(spec.new),
      },
    };
  } else if (spec.old.kind === 'document' && spec.new.kind !== 'document') {
    sources = {
      ...context,
      pattern: 'old_new_mixed',
      oldDocumentId: spec.old.documentId,
      newText: spec.new.text,
      expectedVersions: { old: pinnedVersion(spec.old) },
    };
  } else if (spec.new.kind === 'document' && spec.old.kind !== 'document') {
    sources = {
      ...context,
      pattern: 'old_new_mixed',
      oldText: spec.old.text,
      newDocumentId: spec.new.documentId,
      expectedVersions: { new: pinnedVersion(spec.new) },
    };
  } else if (spec.old.kind !== 'document' && spec.new.kind !== 'document') {
    sources = {
      ...context,
      pattern: 'old_new_text',
      oldText: spec.old.text,
      newText: spec.new.text,
    };
  } else throw new Error('invalid_analysis_input');
  return { ...common, analysisRequest: { sources } };
}
